import { useState, useCallback, useEffect } from "react";
import { X, Plus, Loader2, MapPin } from "lucide-react";
import AddressAutocomplete from "@/components/ui/AddressAutocomplete";
import Select from "@/components/ui/Select";
import useAutoFill from "@/hooks/useAutoFill";
import AutoFillWarning from "@/components/ui/AutoFillWarning";
import MapTilerStatic from "@/components/maps/MapTilerStatic";
import MapBDNB from "@/components/maps/MapBDNB";
import ParcelleSelectionPanel from "@/components/expertise/ParcelleSelectionPanel";
import { reverseParcelle, reverseGeocode, lookupParcelle } from "@/api/geo";
import {
  TYPE_BIEN_OPTIONS,
  TYPE_PROPRIETE_OPTIONS,
  FISCALITE_OPTIONS,
  APPRECIATION_3_OPTIONS,
  FAVORABILITE_OPTIONS,
} from "@/constants/enums";

export default function SectionSituation({
  data,
  onChange,
  onChangeBien,
  formatRapport,
  parcelles,
  onAddParcelle,
  onRemoveParcelle,
  onChangeParcelle: _onChangeParcelle,
}) {
  const [loadingParcelle, setLoadingParcelle] = useState(false);
  const { onAddressSelected, warnings, clearWarnings } = useAutoFill();

  const val = (field) => {
    return field.split(".").reduce((obj, key) => obj?.[key], data) ?? "";
  };

  // Re-fetch la geometrie de la parcelle principale si absente (chargement depuis DB)
  useEffect(() => {
    if (!data?.latitude || !data?.longitude || data?._geometry) return;
    reverseParcelle(data.latitude, data.longitude)
      .then((feature) => {
        if (feature?.geometry) onChange("_geometry", feature.geometry);
      })
      .catch(() => {});
  }, [data?.latitude, data?.longitude, data?._geometry, onChange]);

  const handleAddressSelect = async (addr) => {
    onChange("adresse", addr.adresse);
    onChange("code_postal", addr.code_postal);
    onChange("commune", addr.commune);
    onChange("departement", addr.departement);
    onChange("latitude", addr.latitude);
    onChange("longitude", addr.longitude);
    onChange("cle_interop", addr.cle_interop);
    onChange("code_insee", addr.code_insee);

    // Auto-fill via backend geo APIs
    onAddressSelected(addr);

    // Recherche cadastrale via backend (avec cache + geometrie)
    setLoadingParcelle(true);
    try {
      const feature = await reverseParcelle(addr.latitude, addr.longitude);
      if (feature?.properties) {
        const p = feature.properties;
        onChange("code_insee", p.code_insee);
        onChange("cadastre_prefixe", p.prefixe || "000");
        onChange("cadastre_section", p.section);
        onChange("cadastre_numero", p.numero);
        onChange("contenance_m2", p.contenance);
        onChange("_geometry", feature.geometry);
      }
    } catch {
      // Cadastre non trouve — l'utilisateur peut remplir manuellement
    } finally {
      setLoadingParcelle(false);
    }
  };

  const handleMarkerDragEnd = async ({ latitude, longitude }) => {
    onChange("latitude", latitude);
    onChange("longitude", longitude);

    try {
      const addr = await reverseGeocode(latitude, longitude);
      if (addr) {
        onChange("adresse", addr.label);
        onChange("code_postal", addr.code_postal);
        onChange("commune", addr.commune);
        onChange("departement", addr.departement);
        onChange("code_insee", addr.code_insee);
        onChange("cle_interop", addr.cle_interop);

        onAddressSelected({
          latitude,
          longitude,
          code_postal: addr.code_postal,
          commune: addr.commune,
          code_insee: addr.code_insee,
        });
      }
    } catch {
      // Reverse geocoding failed — coordinates are still updated
    }

    setLoadingParcelle(true);
    try {
      const feature = await reverseParcelle(latitude, longitude);
      if (feature?.properties) {
        const p = feature.properties;
        onChange("code_insee", p.code_insee);
        onChange("cadastre_prefixe", p.prefixe || "000");
        onChange("cadastre_section", p.section);
        onChange("cadastre_numero", p.numero);
        onChange("contenance_m2", p.contenance);
        onChange("_geometry", feature.geometry);
      }
    } catch {
      // Cadastre non trouve
    } finally {
      setLoadingParcelle(false);
    }
  };

  const handleParcelleToggle = useCallback(
    (parcelleData) => {
      const clickId = `${parcelleData.code_insee}${parcelleData.prefixe || "000"}${parcelleData.section}${parcelleData.numero}`;
      const idx = (parcelles || []).findIndex(
        (p) =>
          `${p.code_insee || ""}${p.cadastre_prefixe || "000"}${p.cadastre_section || ""}${p.cadastre_numero || ""}` ===
          clickId
      );

      if (idx >= 0) {
        if (idx === 0) return; // parcelle principale non supprimable
        onRemoveParcelle(idx);
      } else {
        if ((parcelles || []).length >= 20) return;
        onAddParcelle({
          code_insee: parcelleData.code_insee,
          cadastre_prefixe: parcelleData.prefixe || "000",
          cadastre_section: parcelleData.section,
          cadastre_numero: parcelleData.numero,
          contenance_m2: parcelleData.contenance || 0,
          _geometry: parcelleData.geometry,
        });
      }
    },
    [parcelles, onAddParcelle, onRemoveParcelle]
  );

  const [adding, setAdding] = useState(false);
  const [newRow, setNewRow] = useState({ code_insee: "", cadastre_section: "", cadastre_numero: "" });
  const [lookupLoading, setLookupLoading] = useState(false);

  const totalContenance = (parcelles || []).reduce(
    (sum, p) => sum + (Number(p.contenance_m2) || 0),
    0
  );

  const handleManualAdd = async () => {
    const { code_insee, cadastre_section, cadastre_numero } = newRow;
    if (!code_insee || !cadastre_section || !cadastre_numero) return;
    setLookupLoading(true);
    try {
      const result = await lookupParcelle({ code_insee, section: cadastre_section, numero: cadastre_numero });
      const found = result?.parcelles?.[0];
      onAddParcelle({
        code_insee: found?.code_insee || code_insee,
        cadastre_prefixe: found?.prefixe || "000",
        cadastre_section: found?.section || cadastre_section,
        cadastre_numero: found?.numero || cadastre_numero,
        contenance_m2: found?.contenance || 0,
      });
      setNewRow({ code_insee: "", cadastre_section: "", cadastre_numero: "" });
      setAdding(false);
    } catch {
      // Fallback: add with user input
      onAddParcelle({ code_insee, cadastre_prefixe: "000", cadastre_section, cadastre_numero, contenance_m2: 0 });
      setNewRow({ code_insee: "", cadastre_section: "", cadastre_numero: "" });
      setAdding(false);
    } finally {
      setLookupLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Localisation */}
      <h3 className="text-lg font-semibold text-slate-800 border-b pb-2">
        Localisation
      </h3>
      <AddressAutocomplete
        label="Adresse"
        name="adresse"
        value={val("adresse")}
        onChange={(value) => onChange("adresse", value)}
        onSelect={handleAddressSelect}
        required
      />
      <AutoFillWarning warnings={warnings} onDismiss={clearWarnings} />

      {/* Carte + panneau parcelles cote a cote */}
      {data?.latitude && data?.longitude && (
        <div className="flex gap-4">
          {/* Carte — 2/3 */}
          <div className="flex-1 min-w-0">
            <MapBDNB
              latitude={data.latitude}
              longitude={data.longitude}
              height="500px"
              selectedParcelles={parcelles || []}
              onParcelleToggle={handleParcelleToggle}
              onMarkerDragEnd={handleMarkerDragEnd}
            />
          </div>

          {/* Panneau parcelles — droite */}
          <div className="w-72 shrink-0 flex flex-col border border-slate-200 rounded-lg bg-white">
            <div className="px-3 py-2 border-b border-slate-100 flex items-center justify-between">
              <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
                <MapPin className="w-4 h-4 text-slate-400" />
                Parcelles ({(parcelles || []).length})
              </h4>
              {loadingParcelle && (
                <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />
              )}
            </div>

            <div className="flex-1 overflow-y-auto p-2 space-y-2" style={{ maxHeight: "400px" }}>
              {(parcelles || []).length === 0 && (
                <p className="text-xs text-slate-400 text-center py-4">
                  Cliquez sur la carte pour selectionner une parcelle
                </p>
              )}
              {(parcelles || []).map((p, idx) => (
                <div
                  key={p.id || `p-${idx}`}
                  className={`rounded-lg border p-2.5 text-sm transition-colors ${
                    idx === 0
                      ? "border-carex-200 bg-carex-50"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <span className="font-medium text-slate-900">
                        Section {p.cadastre_section || "?"} n&deg;{p.cadastre_numero || "?"}
                      </span>
                      {idx === 0 && (
                        <span className="ml-1.5 text-[10px] font-medium text-carex-600 bg-carex-100 px-1.5 py-0.5 rounded">
                          principale
                        </span>
                      )}
                    </div>
                    {idx > 0 && (
                      <button
                        type="button"
                        onClick={() => onRemoveParcelle(idx)}
                        className="text-slate-400 hover:text-red-500 transition-colors p-0.5"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {p.contenance_m2 ? Number(p.contenance_m2).toLocaleString("fr-FR") : "0"} m²
                    {p.cadastre_prefixe && p.cadastre_prefixe !== "000" && (
                      <span className="ml-1">— prefixe {p.cadastre_prefixe}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Footer : total + ajout */}
            <div className="border-t border-slate-100 px-3 py-2 space-y-2">
              {(parcelles || []).length > 0 && (
                <div className="flex justify-between text-xs">
                  <span className="text-slate-500">Total</span>
                  <span className="font-semibold text-slate-800">
                    {totalContenance.toLocaleString("fr-FR")} m²
                  </span>
                </div>
              )}

              {adding ? (
                <div className="space-y-1.5">
                  <div className="flex gap-1">
                    <input
                      className="w-full rounded border-slate-300 text-xs px-2 py-1"
                      placeholder="INSEE"
                      maxLength={5}
                      value={newRow.code_insee}
                      onChange={(e) => setNewRow({ ...newRow, code_insee: e.target.value })}
                    />
                    <input
                      className="w-full rounded border-slate-300 text-xs px-2 py-1"
                      placeholder="Section"
                      maxLength={2}
                      value={newRow.cadastre_section}
                      onChange={(e) => setNewRow({ ...newRow, cadastre_section: e.target.value.toUpperCase() })}
                    />
                    <input
                      className="w-full rounded border-slate-300 text-xs px-2 py-1"
                      placeholder="Numero"
                      maxLength={4}
                      value={newRow.cadastre_numero}
                      onChange={(e) => setNewRow({ ...newRow, cadastre_numero: e.target.value })}
                    />
                  </div>
                  <div className="flex gap-1.5">
                    <button
                      type="button"
                      onClick={handleManualAdd}
                      disabled={lookupLoading}
                      className="flex-1 text-xs bg-carex-600 text-white px-2 py-1 rounded hover:bg-carex-700 disabled:opacity-50 flex items-center justify-center gap-1"
                    >
                      {lookupLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : "Rechercher"}
                    </button>
                    <button
                      type="button"
                      onClick={() => setAdding(false)}
                      className="text-xs text-slate-500 hover:text-slate-700 px-2"
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setAdding(true)}
                  className="w-full flex items-center justify-center gap-1 text-xs text-carex-600 hover:text-carex-700 font-medium py-1"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Ajouter une parcelle
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Panel batiments BDNB */}
      <ParcelleSelectionPanel
        selectedParcelles={(parcelles || []).map((p) => ({
          id: `${p.code_insee || ""}${p.cadastre_prefixe || "000"}${p.cadastre_section || ""}${p.cadastre_numero || ""}`,
          code_insee: p.code_insee,
          prefixe: p.cadastre_prefixe,
          section: p.cadastre_section,
          numero: p.cadastre_numero,
          contenance: p.contenance_m2,
        }))}
        onRemoveParcelle={(id) => {
          const idx = (parcelles || []).findIndex(
            (p) =>
              `${p.code_insee || ""}${p.cadastre_prefixe || "000"}${p.cadastre_section || ""}${p.cadastre_numero || ""}` ===
              id
          );
          if (idx > 0) onRemoveParcelle(idx);
        }}
      />

      {/* Cartes MapTiler */}
      {data?.latitude && data?.longitude && (
        <MapTilerStatic latitude={data.latitude} longitude={data.longitude} parcelleGeometry={data._geometry} />
      )}

      {/* Propriete */}
      <h3 className="text-lg font-semibold text-slate-800 border-b pb-2">
        Propriete
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Select
          label="Type de bien"
          name="type_bien"
          options={TYPE_BIEN_OPTIONS}
          value={data?.biens?.[0]?.type_bien ?? ""}
          onChange={(e) => onChangeBien("type_bien", e.target.value)}
          required
          placeholder="Sélectionner un type"
        />
        <Select
          label="Type de propriete"
          name="type_propriete"
          options={TYPE_PROPRIETE_OPTIONS}
          value={val("type_propriete")}
          onChange={(e) => onChange("type_propriete", e.target.value)}
        />
        <Select
          label="Fiscalite"
          name="fiscalite"
          options={FISCALITE_OPTIONS}
          value={val("fiscalite")}
          onChange={(e) => onChange("fiscalite", e.target.value)}
        />
      </div>

      {/* BCG only */}
      {formatRapport === "bcg" && (
        <>
          <h3 className="text-lg font-semibold text-slate-800 border-b pb-2">
            Champs BCG
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Evolution demographique"
              name="situation.evolution_demographique"
              options={APPRECIATION_3_OPTIONS}
              value={val("situation.evolution_demographique")}
              onChange={(e) =>
                onChange("situation.evolution_demographique", e.target.value)
              }
            />
            <Select
              label="Favorabilite"
              name="situation.favorabilite"
              options={FAVORABILITE_OPTIONS}
              value={val("situation.favorabilite")}
              onChange={(e) =>
                onChange("situation.favorabilite", e.target.value)
              }
            />
          </div>
        </>
      )}
    </div>
  );
}
